import { CommonModule } from '@angular/common';
import { Component, EventEmitter, HostListener, Input, Output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AppAlertService } from '@shared/service/app/alert.service';
import { Router } from '@angular/router';
import { PwaService } from '@shared/service/app/pwa.service';
import { environment } from 'environments/environment';

type AdjuntoGuardado = { name: string; url?: string; };

@Component({
    selector: 'app-adjuntos',
    templateUrl: './adjuntos.component.html',
    standalone: true,
    imports: [CommonModule, MatIconModule, MatButtonModule, MatTooltipModule]
})
export class AdjuntosComponent {
    @Input() titulo: string = 'Agregar Adjuntos';
    @Input() mensajeVacio: string = 'No hay archivos cargados';
    @Input() maxArchivos: number = 1;
    @Input() extensionesPermitidas: string[] = ['.pdf'];
    @Input() maxSizeMb: number = 20;
    @Input() archivosGuardados: AdjuntoGuardado[] = [];
    @Input() soloLectura: boolean = false;
    @Input() color_primario: string;
    @Input() permitirPegar: boolean = false;
    @Input() mostrarPreview: boolean = false;
    @Input() mostrarListaArchivos: boolean = true;

    @Output() archivosChange = new EventEmitter<any[]>();
    @Output() eliminarAdjuntoGuardado = new EventEmitter<string>();

    selectedFiles: File[] = [];
    adjuntosNuevos: { name: string; extension: string; base64: string }[] = [];
    titleAdjunto = false;
    baseRest = environment.baseRest;

    constructor(
        public alert: AppAlertService,
        private router: Router,
        private pwaService: PwaService
    ) {}

    openAdjunto(adj: AdjuntoGuardado) {
        const rawUrl = adj.url;
        if (!rawUrl) {
            this.alert.warning('No hay una ruta válida para este adjunto.');
            return;
        }

        try {
            const normalized = this.normalizeAdjuntoUrl(rawUrl);
            if (this.pwaService.isPwa()) {
                const uploadsIndex = normalized.indexOf('/uploads');
                if (uploadsIndex === -1) {
                    this.alert.warning('Ruta inválida para PWA (no contiene "/uploads").');
                    return;
                }

                const relativePath = normalized.substring(uploadsIndex + 1);
                this.router.navigate(['/reporte', relativePath]);
            } else {
                const url = this.buildAdjuntoUrl(normalized);
                window.open(url, '_blank', 'noopener');
            }
        } catch (err) {
            console.error('Error al abrir adjunto', err);
            this.alert.error('No se pudo abrir el archivo.');
        }
    }

    openNuevoAdjunto(file: File) {
        try {
            const url = URL.createObjectURL(file);
            window.open(url, '_blank', 'noopener');
        } catch (err) {
            console.error('Error al abrir adjunto nuevo', err);
            this.alert.error('No se pudo previsualizar el archivo nuevo.');
        }
    }

    // === Adjuntar nuevos (respeta soloLectura) ===
    onAddAdjunto(event: Event) {
        if (this.soloLectura) return;
        const archivos: FileList = (event.target as HTMLInputElement).files!;
        if (!archivos || archivos.length === 0) return;

        for (let i = 0; i < archivos.length; i++) {
            this.procesarArchivo(archivos[i]);
        }

        const input = event.target as HTMLInputElement;
        if (input) {
            input.value = '';
        }
    }

    /**
     * Escucha el evento de pegado global para capturar imágenes del portapapeles
     */
    @HostListener('window:paste', ['$event'])
    onPaste(event: ClipboardEvent): void {
        if (this.soloLectura || !this.permitirPegar) return;

        const items = event.clipboardData?.items;
        if (!items) return;

        for (let i = 0; i < items.length; i++) {
            if (items[i].type.indexOf('image') !== -1) {
                const blob = items[i].getAsFile();
                if (!blob) continue;

                // Crear un archivo con nombre único para la captura
                const timestamp = new Date().getTime();
                const file = new File([blob], `captura_${timestamp}.png`, { type: blob.type });
                
                this.procesarArchivo(file);
            }
        }
    }

    /**
     * Procesa un archivo individual validando tamaño, extensión y convirtiéndolo a Base64
     */
    private procesarArchivo(archivo: File): void {
        if (this.selectedFiles.length >= this.maxArchivos) {
            this.alert.info(`Solo se permite adjuntar hasta ${this.maxArchivos} archivo(s).`);
            return;
        }

        const extension = '.' + archivo.name.split('.').pop()?.toLowerCase();
        const sizeMb = archivo.size / 1024 / 1024;
        const maxBytes = this.maxSizeMb * 1024 * 1024;

        if (!this.extensionesPermitidas.includes(extension)) {
            this.alert.info(`Formato no permitido (${extension})`);
            return;
        }
        
        if (archivo.size > maxBytes) {
            const sizeMbRounded = Math.round(sizeMb * 100) / 100;
            this.alert.warning(`El archivo supera ${this.maxSizeMb} Mb (${sizeMbRounded} Mb): ${archivo.name}`);
            return;
        }

        this.selectedFiles.push(archivo);

        this.toBase64(archivo).then(base64 => {
            const adj = { name: archivo.name, extension, base64 };
            this.adjuntosNuevos.push(adj);
            this.titleAdjunto = true;
            this.archivosChange.emit(this.adjuntosNuevos);
        });
    }

    toBase64(file: File): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result?.toString().split('base64,')[1] || '');
            reader.onerror = error => reject(error);
        });
    }

    getFileSize(size: number): string {
        const kb = size / 1024;
        return kb < 1024 ? `${kb.toFixed(1)} KB` : `${(kb / 1024).toFixed(1)} MB`;
    }

    getFileTitle(file: File): string {
        return `${file.name} (${this.getFileSize(file.size)})`;
    }

    removeFile(tipo: 'nuevo' | 'guardado', file: any) {
        if (this.soloLectura) return;

        if (tipo === 'nuevo') {
            this.selectedFiles = this.selectedFiles.filter(f => f.name !== file.name);
            this.adjuntosNuevos = this.adjuntosNuevos.filter(a => a.name !== file.name);
            this.archivosChange.emit(this.adjuntosNuevos);
        }

        if (tipo === 'guardado') {
            this.eliminarAdjuntoGuardado.emit(file.name);
        }
    }

    selectedPreview: string | null = null;

    /**
     * Determina si un archivo es una imagen basándose en su extensión
     */
    isImage(extension: string): boolean {
        return ['.png', '.jpg', '.jpeg', '.gif', '.webp'].includes(extension.toLowerCase());
    }

    /**
     * Obtiene el data URL para la previsualización de una imagen nueva
     */
    getPreviewUrl(base64: string, extension: string): string {
        const type = extension.replace('.', '');
        return `data:image/${type};base64,${base64}`;
    }

    private normalizeAdjuntoUrl(rawUrl: string): string {
        let trimmed = rawUrl.trim();
        trimmed = trimmed.replace(/\/webuploads(?=\/|$)/i, '/web/uploads');
        trimmed = trimmed.replace(/(^|\/)webuploads(?=\/|$)/i, '$1web/uploads');
        return trimmed;
    }

    private buildAdjuntoUrl(rawUrl: string): string {
        const normalized = rawUrl.trim();
        if (/^https?:\/\//i.test(normalized)) {
            return normalized;
        }

        let path = normalized.startsWith('/') ? normalized : `/${normalized}`;
        const base = (this.baseRest ?? '').replace(/\/+$/, '');
        if (/\/web$/i.test(base) && path.startsWith('/web/')) {
            path = path.replace(/^\/web/i, '');
        }
        return `${base}${path}`;
    }
}
